import React, { useEffect } from 'react';

import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import Icon from 'shared/atoms/Icon';
import Pill from 'shared/atoms/Pill';
import T from 'shared/atoms/Typography';
import Tooltip from 'shared/atoms/Tooltip';

import theme from 'shared/themes/default';

import TableWithSearchAndExport from './TableWithSearchAndExport';
import OrganizationGroupsStatsTable from './OrganizationGroupsStatsTable';
import UsersWatchDataTable from './UsersWatchDataTable';
import EpisodesSeenGraph from './EpisodesSeenGraph';

import { useWatchData } from '../Context';
import {
  listAggregatedWatchData,
  listGroupWatchData,
} from '../Context/graphql/queries';

const Wrapper = styled.div`
  max-width: 100%;
`;

const WrapperPill = styled.div`
  display: flex;
  margin-top: 108px;
  ${theme.breakpoints.mobile} {
    margin-top: 64px;
  }
`;

const StyledIconWrapper = styled.div`
  margin-left: 0.5rem;
  display: flex;
  align-items: center;
`;

const ShowGroupsData = () => {
  const { t } = useTranslation();
  const {
    id,
    isOrganizationManager,
    fetchGroups,
    organizationGroupsStats,
    loadingGroups,
    groupSelected,
    setGroupSelected,
    onSelectItem,
    organizationSelectedId,
    restClient,
    setWatchDataError,
    useLazyGraphQLQuery,
  } = useWatchData();

  const [listAggregatedWatchDataQuery, listAggregatedWatchDataResponse] =
    useLazyGraphQLQuery(listAggregatedWatchData);
  const [listGroupWatchDataQuery, listGroupWatchDataResponse] =
    useLazyGraphQLQuery(listGroupWatchData);

  const aggregatedWatchData =
    listAggregatedWatchDataResponse.data?.listAggregatedWatchData || [];
  const groupUsersWatchData =
    listGroupWatchDataResponse.data?.listGroupWatchData || [];

  useEffect(() => {
    const { error } = listAggregatedWatchDataResponse;

    if (error) {
      setWatchDataError(true);
    }
  }, [listAggregatedWatchDataResponse]);

  useEffect(() => {
    const { error } = listGroupWatchDataResponse;

    if (error) {
      setWatchDataError(true);
    }
  }, [listGroupWatchDataResponse]);

  useEffect(() => {
    const filter = { groupId: 'all', organizationId: organizationSelectedId };
    const variables = { organizationId: organizationSelectedId };

    if (groupSelected) {
      filter.groupId = groupSelected.id;
      variables.groupId = groupSelected.id;

      listGroupWatchDataQuery({ variables });
    } else {
      fetchGroups({ variables });
    }

    listAggregatedWatchDataQuery({
      variables: {
        filter,
      },
    });
  }, [groupSelected, organizationSelectedId]);

  useEffect(() => {
    if (id && !loadingGroups && organizationGroupsStats) {
      const group = organizationGroupsStats.find((item) => item.id === id);
      if (group) {
        setGroupSelected(group);
      } else {
        onSelectItem();
      }
    }
  }, [id, organizationGroupsStats, loadingGroups]);

  return (
    <Wrapper>
      <WrapperPill>
        <Pill
          color={theme.palette.lightPure}
          fontColor={theme.palette.mainDark}
        >
          {t('administrateUsers.groups').toUpperCase()}
        </Pill>
        <Tooltip title={t('watchData.titleTooltip')} arrow>
          <StyledIconWrapper>
            <Icon
              color={theme.palette.lightPure}
              colorHover={theme.palette.brand}
              icon={InfoOutlinedIcon}
              size={theme.spacing.m2}
              sx={{
                cursor: 'pointer',
              }}
            />
          </StyledIconWrapper>
        </Tooltip>
      </WrapperPill>
      <T
        color={theme.palette.brand}
        fontWeight="medium"
        phoneVariant="headingXs"
        sx={{
          marginBottom: theme.spacing.m3,
          marginTop: theme.spacing.m2,
        }}
        variant="headingS"
      >
        {groupSelected ? groupSelected.name : t('administrateUsers.allGroups')}
      </T>
      <EpisodesSeenGraph
        data={aggregatedWatchData}
        loading={listAggregatedWatchDataResponse.loading}
      />
      {!groupSelected && (
        <TableWithSearchAndExport
          itemsToDisplay={organizationGroupsStats || []}
          downloadItems={() =>
            restClient.get(
              `/watchData/organizations/${organizationSelectedId}/groups`
            )
          }
          tableTitle={t('watchData.activitiesOfYourGroups')}
          searchPlaceHolder={t('watchData.searchForGroup')}
          TableToDisplay={OrganizationGroupsStatsTable}
          tableProps={{
            isOrganizationManager,
            selectGroupsHandler: onSelectItem,
          }}
          showRowsAreClickableMessage
          loading={loadingGroups}
          spinnerDuration={15}
          emptyMessage={t('watchData.noActivity')}
        />
      )}
      {groupSelected && (
        <TableWithSearchAndExport
          itemsToDisplay={groupUsersWatchData}
          downloadItems={() =>
            restClient.get(
              `/watchData/organizations/${organizationSelectedId}/groups/${groupSelected.id}`
            )
          }
          tableTitle={t('watchData.activityOfYourUsers')}
          searchPlaceHolder={t('watchData.searchForUsers')}
          TableToDisplay={UsersWatchDataTable}
          showRowsAreClickableMessage
          emptyMessage={t('watchData.noGroupActivity')}
          loading={listGroupWatchDataResponse.loading}
          tableProps={{
            organizationSelectedId,
          }}
        />
      )}
    </Wrapper>
  );
};

export default ShowGroupsData;
